import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Tabs } from "@package/subframe/components/Tabs";
import {
  FeatherArrowLeft,
  FeatherBuilding,
  FeatherPencil,
} from "@subframe/core";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

import { BranchFormDialog } from "@/components/BranchesTable/BranchFormDialog";
import { BranchPinLoginToggle } from "@/components/BranchesTable/BranchPinLoginToggle";
import { WorkspaceStatusBadge } from "@/components/checklists/WorkspaceStatusBadge";
import { ClosuresTable } from "@/components/ClosuresTable";
import { ColdStorageTable } from "@/components/ColdStorageTable";
import { EmergencyLightTable } from "@/components/EmergencyLightTable";
import { EventAcknowledgementRulesTab } from "@/components/event-acknowledgement/EventAcknowledgementRulesTab";
import { FireAlarmCallPointTable } from "@/components/FireAlarmCallPointTable";
import { FireDoorTable } from "@/components/FireDoorTable";
import { FireExtinguisherTable } from "@/components/FireExtinguisherTable";
import { RoomsTable } from "@/components/RoomsTable";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import type { NurseryResponseDtoStatus } from "@/generated/model";
import { useGetNurseryQueryControllerGet } from "@/generated/nurseries/nurseries";
import { nurseryStatusPill } from "@/lib/workspace/status-pill";

export const Route = createFileRoute(
  "/_authenticated/organisations/$slug/branches/$branchId",
)({
  component: BranchDetailPage,
});

function BranchDetailPage() {
  const { slug, branchId } = Route.useParams();
  const { isPlatformAdmin } = useAuth();
  const { isOrgAdmin } = useOrganisation();
  const canManageNurseries = isPlatformAdmin || isOrgAdmin;
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    | "details"
    | "rooms"
    | "cold-storage"
    | "fire-extinguishers"
    | "emergency-lights"
    | "fire-alarm-call-points"
    | "fire-doors"
    | "closures"
    | "event-acknowledgement"
  >("rooms");

  const {
    data: branchData,
    isLoading,
    error,
  } = useGetNurseryQueryControllerGet(branchId);

  const branch = branchData?.data;

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  if (error || !branch) {
    return (
      <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
        <IconWithLogoBackground variant="error" size="x-large" />
        <span className="text-heading-3 font-heading-3 text-default-font">
          Nursery not found
        </span>
        <Link
          to="/organisations/$slug/branches"
          params={{ slug }}
          className="text-body font-body text-brand-primary hover:underline"
        >
          Back to nurseries
        </Link>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-6 animate-fade-in">
      {/* Header with back link */}
      <div className="flex w-full items-center gap-4">
        <Link to="/organisations/$slug/branches" params={{ slug }}>
          <IconButton
            variant="neutral-tertiary"
            size="medium"
            icon={<FeatherArrowLeft />}
          />
        </Link>
        <div className="flex grow shrink-0 basis-0 items-center gap-3">
          <IconWithLogoBackground
            variant="neutral"
            size="medium"
            icon={<FeatherBuilding />}
          />
          <div className="flex flex-col items-start">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {branch.name}
            </span>
            <span className="text-body font-body text-subtext-color">
              {branch.address.street}, {branch.address.city},{" "}
              {branch.address.postcode}
            </span>
          </div>
          <WorkspaceStatusBadge pill={nurseryStatusPill(branch.status)} />
        </div>
        {canManageNurseries && (
          <Button
            variant="brand-primary"
            icon={<FeatherPencil />}
            onClick={() => setEditDialogOpen(true)}
          >
            Edit Nursery
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs>
        <Tabs.Item
          active={activeTab === "details"}
          onClick={() => setActiveTab("details")}
        >
          Details
        </Tabs.Item>
        <Tabs.Item
          active={activeTab === "rooms"}
          onClick={() => setActiveTab("rooms")}
        >
          Rooms
        </Tabs.Item>
        <Tabs.Item
          active={activeTab === "cold-storage"}
          onClick={() => setActiveTab("cold-storage")}
        >
          Fridges &amp; Freezers
        </Tabs.Item>
        <Tabs.Item
          active={activeTab === "fire-extinguishers"}
          onClick={() => setActiveTab("fire-extinguishers")}
        >
          Fire Extinguishers
        </Tabs.Item>
        <Tabs.Item
          active={activeTab === "emergency-lights"}
          onClick={() => setActiveTab("emergency-lights")}
        >
          Emergency Lights
        </Tabs.Item>
        <Tabs.Item
          active={activeTab === "fire-alarm-call-points"}
          onClick={() => setActiveTab("fire-alarm-call-points")}
        >
          Call Points
        </Tabs.Item>
        <Tabs.Item
          active={activeTab === "fire-doors"}
          onClick={() => setActiveTab("fire-doors")}
        >
          Fire Doors
        </Tabs.Item>
        <Tabs.Item
          active={activeTab === "closures"}
          onClick={() => setActiveTab("closures")}
        >
          Closures
        </Tabs.Item>
        <Tabs.Item
          active={activeTab === "event-acknowledgement"}
          onClick={() => setActiveTab("event-acknowledgement")}
        >
          Event acknowledgement
        </Tabs.Item>
      </Tabs>

      {activeTab === "details" && (
        <div className="flex w-full flex-col gap-6">
          <BranchDetailsTab branch={branch} canEdit={canManageNurseries} />
          <BranchPinLoginToggle
            nurseryId={branchId}
            pinLoginEnabled={branch.pinLoginEnabled}
            canManage={canManageNurseries}
          />
        </div>
      )}
      {activeTab === "rooms" && <RoomsTable nurseryId={branchId} />}
      {activeTab === "cold-storage" && (
        <ColdStorageTable nurseryId={branchId} />
      )}
      {activeTab === "fire-extinguishers" && (
        <FireExtinguisherTable nurseryId={branchId} />
      )}
      {activeTab === "emergency-lights" && (
        <EmergencyLightTable nurseryId={branchId} />
      )}
      {activeTab === "fire-alarm-call-points" && (
        <FireAlarmCallPointTable nurseryId={branchId} />
      )}
      {activeTab === "fire-doors" && <FireDoorTable nurseryId={branchId} />}
      {activeTab === "closures" && (
        <ClosuresTable nurseryId={branchId} canManage={canManageNurseries} />
      )}
      {activeTab === "event-acknowledgement" && (
        <EventAcknowledgementRulesTab
          nurseryId={branchId}
          canManage={canManageNurseries}
        />
      )}

      {/* Edit Nursery Dialog */}
      {branch.organisationId && (
        <BranchFormDialog
          organisationId={branch.organisationId}
          branchId={branchId}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
        />
      )}
    </div>
  );
}

function BranchDetailsTab({
  branch,
  canEdit,
}: {
  branch: {
    name: string;
    address: {
      street: string;
      city: string;
      postcode: string;
      country: string;
    };
    status: NurseryResponseDtoStatus;
    createdAt: string;
    updatedAt: string;
  };
  canEdit: boolean;
}) {
  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-4 rounded-md border border-solid border-secondary-beige-200 p-6">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Nursery Information
        </span>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-caption font-caption text-subtext-color">
              Name
            </span>
            <span className="text-body font-body text-default-font">
              {branch.name}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-caption font-caption text-subtext-color">
              Status
            </span>
            <span className="text-body font-body text-default-font">
              {nurseryStatusPill(branch.status).label}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-caption font-caption text-subtext-color">
              Street
            </span>
            <span className="text-body font-body text-default-font">
              {branch.address.street}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-caption font-caption text-subtext-color">
              City
            </span>
            <span className="text-body font-body text-default-font">
              {branch.address.city}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-caption font-caption text-subtext-color">
              Postcode
            </span>
            <span className="text-body font-body text-default-font">
              {branch.address.postcode}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-caption font-caption text-subtext-color">
              Country
            </span>
            <span className="text-body font-body text-default-font">
              {branch.address.country}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-caption font-caption text-subtext-color">
              Created
            </span>
            <span className="text-body font-body text-default-font">
              {new Date(branch.createdAt).toLocaleDateString()}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-caption font-caption text-subtext-color">
              Last Updated
            </span>
            <span className="text-body font-body text-default-font">
              {new Date(branch.updatedAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        {!canEdit && (
          <span className="text-caption font-caption text-subtext-color italic">
            Contact your nursery group admin to edit nursery details.
          </span>
        )}
      </div>
    </div>
  );
}
